// SearchContext.jsx
'use client';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { trackEvent } from '../lib/analytics/posthog';
import { EVENTS } from '../lib/analytics/events';

const SearchContext = createContext();

export const useSearch = () => useContext(SearchContext);

// Motivos por los que «Usar mi ubicación actual» puede fallar, para mostrarle al usuario qué pasó.
export const GEOLOCATION_ERRORS = {
  denied: 'denied',
  unavailable: 'unavailable',
  timeout: 'timeout',
  unsupported: 'unsupported'
};

export const SearchProvider = ({ children }) => {
  const router = useRouter();
  const [locationPermission, setLocationPermission] = useState(false);
  const [searchBarParams, setSearchBarParams] = useState(null);
  const [userLocation, setUserLocation] = useState({ lat: null, long: null });
  // Nombre de la zona elegida («Palermo», «Ubicación actual»). La URL solo lleva coordenadas:
  // sin esto, el buscador del header quedaba vacío al llegar a /negocios.
  const [locationLabel, setLocationLabel] = useState('');
  // false hasta leer la URL: /negocios no debe buscar con la ubicación por defecto y enseguida
  // con la de la URL.
  const [isReady, setIsReady] = useState(false);
  const [params, setParams] = useState({
    query: '',
    lat: '',
    lng: '',
    distance: '10'
  });

  const getParams = () => {
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      return {
        query: searchParams.get('query') || '',
        lat: searchParams.get('lat') || '',
        lng: searchParams.get('lng') || '',
        distance: searchParams.get('distance') || '10'
      };
    }
    return {
      query: '',
      lat: '',
      lng: '',
      distance: '10'
    };
  };

  // window.location no existe en el servidor: los parámetros se leen recién montado, para que el HTML
  // hidrate igual que el del servidor.
  useEffect(() => {
    const newParams = getParams();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setParams(newParams);
    setIsReady(true);
  }, []);

  const updateUrlParam = (param, value) => {
    const searchParams = new URLSearchParams(window.location.search);
    if (value) {
      searchParams.set(param, value);
    } else {
      searchParams.delete(param);
    }
    const newPath = `/negocios/?${searchParams.toString()}`;
    router.push(newPath);
    setParams(getParams());
  };

  const updateUrlParams = (newParams) => {
    const searchParams = new URLSearchParams(window.location.search);
    for (const param in newParams) {
      if (newParams[param]) {
        searchParams.set(param, newParams[param]);
      } else {
        searchParams.delete(param);
      }
    }
    const newPath = `/negocios/?${searchParams.toString()}`;
    router.push(newPath);
    setParams({
      ...getParams(),
      ...newParams
    });
  };

  /**
   * Pide la ubicación del dispositivo. Solo la guarda si el navegador la da: con el permiso
   * denegado antes se escribía «Ubicación actual» igual, sin coordenadas detrás.
   * @returns {Promise<{ ok: true, lat: number, lng: number } | { ok: false, reason: string }>}
   */
  const requestLocation = () =>
    new Promise((resolve) => {
      if (locationPermission && userLocation.lat && userLocation.long) {
        resolve({ ok: true, lat: userLocation.lat, lng: userLocation.long });
        return;
      }

      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        setLocationPermission(false);
        resolve({ ok: false, reason: GEOLOCATION_ERRORS.unsupported });
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, long: longitude });
          setLocationPermission(true);
          trackEvent(EVENTS.SEARCH_GPS_REQUESTED, { granted: true });
          resolve({ ok: true, lat: latitude, lng: longitude });
        },
        (error) => {
          setLocationPermission(false);
          trackEvent(EVENTS.SEARCH_GPS_REQUESTED, { granted: false });
          const reason =
            error?.code === 1
              ? GEOLOCATION_ERRORS.denied
              : error?.code === 3
                ? GEOLOCATION_ERRORS.timeout
                : GEOLOCATION_ERRORS.unavailable;
          resolve({ ok: false, reason });
        },
        { timeout: 10000, maximumAge: 5 * 60 * 1000 }
      );
    });

  const contextValue = {
    updateUrlParam,
    updateUrlParams,
    searchBarParams,
    setSearchBarParams,
    params,
    setParams,
    isReady,
    locationLabel,
    setLocationLabel,
    locationPermission,
    userLocation,
    requestLocation
  };

  return <SearchContext.Provider value={contextValue}>{children}</SearchContext.Provider>;
};
